import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import CybsIcon from '@cybs-ui/components/lib/icon'
import {replaceSpace, setWindowLocationHash} from '../../../../../libs/common-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import {getFirstLevelKey, getSecondLevelKey, getThirdLevelKey} from '../../../reducers/examples-reducer';
import {getDescriptionExpansionAlertFlag} from '../../../reducers/rhs-schema-reducer';
import '../css/descriptions.css';
import { putDataInDevCenterDataStore } from '../../../../../app/store';
import {getPartnerFullName } from '../../../../../lhs/reducers/configuration-reducer';

const renderer = new marked.Renderer();

renderer.heading = (text, level) => {
    return `<h${level} style="margin-left:20px">
              ${text}
            </h${level}>`
  };

renderer.paragraph = (text) => {
    return `<p style="margin-left:20px">
              ${text}
            </p>`
  }

marked.setOptions({
    renderer
});

const ProgressiveDescriptions = React.memo(({ jsTree, display, parent, type }) => {
    // State-based expansion tracking
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    
    // Progressive loading state
    const [loadedItems, setLoadedItems] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    
    // Processed descriptions cache
    const [processedDescriptions, setProcessedDescriptions] = useState(new Map());
    
    // Refs for cleanup and cancellation
    const loadingRef = useRef(false);
    const cancelLoadingRef = useRef(false);
    const timeoutRefs = useRef([]);
    
    // Constants for progressive loading
    const BATCH_SIZE = 12; // Items per batch
    const BATCH_DELAY = 16; // ms between batches (60fps)

    // Memoized partner name
    const partnerFullName = useMemo(() => {
        return getPartnerFullName().length > 0 ? getPartnerFullName() : 'Cybersource';
    }, []);

    // Flatten tree structure with parent tracking
    const flattenedItems = useMemo(() => {
        if (!jsTree || jsTree.length === 0) return [];
        
        const flatten = (nodes, level = 0, parentPath = []) => {
            const items = [];
            nodes.forEach(node => {
                const currentPath = [...parentPath, node.value];
                items.push({ ...node, level, parentPath });
                if (node.children && expandedNodes.has(node.value)) {
                    items.push(...flatten(node.children, level + 1, currentPath));
                }
            });
            return items;
        };
        
        const flattened = flatten(jsTree);
        return flattened;
    }, [jsTree, expandedNodes]);

    // Memoized function to check if item should show border and calculate border positions
    const getBorderInfo = useCallback((item) => {
        const result = {
            showOwnBorder: false,
            parentBorders: [] // Array of all parent borders to show
        };
        
        // Check if the item itself is expanded
        if (expandedNodes.has(item.value)) {
            result.showOwnBorder = true;
        }
        
        // Check ALL parents in the path and collect borders for expanded ones
        if (item.parentPath) {
            for (let i = 0; i < item.parentPath.length; i++) {
                const parentValue = item.parentPath[i];
                if (expandedNodes.has(parentValue)) {
                    // Calculate the offset needed to align with this expanded parent
                    const parentLevel = i; // Parent's level in the hierarchy
                    const currentLevel = item.level;
                    const levelDifference = currentLevel - parentLevel;
                    const parentBorderOffset = -(levelDifference * 20); // Negative offset to move border left
                    
                    result.parentBorders.push({
                        offset: parentBorderOffset,
                        parentValue: parentValue
                    });
                }
            }
        }
        
        return result;
    }, [expandedNodes]);

    // Progressive loading function - Load all items immediately but process markdown progressively
    const startProgressiveLoading = useCallback(async () => {
        if (loadingRef.current || !flattenedItems.length) return;
        
        loadingRef.current = true;
        cancelLoadingRef.current = false;
        
        // Load all items immediately to prevent scroll jumping
        setLoadedItems(new Set(flattenedItems.map(item => item.value)));
        
        try {
            // Process markdown in batches in background
            for (let i = 0; i < flattenedItems.length; i += BATCH_SIZE) {
                // Check for cancellation
                if (cancelLoadingRef.current) {
                    break;
                }
                
                const batch = flattenedItems.slice(i, i + BATCH_SIZE);
                const batchProcessedDescriptions = new Map();
                
                // Process batch items
                batch.forEach(item => {
                    // Process markdown if description exists
                    if (item.description) {
                        try {
                            const processedHtml = DOMPurify.sanitize(marked(
                                item.description.replaceAll(/Cybersource/ig, partnerFullName)
                            ));
                            batchProcessedDescriptions.set(item.value, processedHtml);
                        } catch (error) {
                            console.error(`Markdown processing failed for ${item.value}:`, error);
                            batchProcessedDescriptions.set(item.value, item.description || '');
                        }
                    }
                });
                
                // Update processed descriptions
                setProcessedDescriptions(prev => new Map([...prev, ...batchProcessedDescriptions]));
                                
                // Yield control to prevent UI blocking
                if (i + BATCH_SIZE < flattenedItems.length) {
                    await new Promise(resolve => {
                        const timeoutId = setTimeout(resolve, BATCH_DELAY);
                        timeoutRefs.current.push(timeoutId);
                    });
                }
            }
        } catch (error) {
            console.error('Description loading error:', error);
        } finally {
            loadingRef.current = false;
        }
    }, [flattenedItems, partnerFullName]);

    // Start loading when component mounts or items change
    useEffect(() => {
        if (flattenedItems.length > 0) {
            // Reset loading state
            setLoadedItems(new Set());
            setProcessedDescriptions(new Map());
            
            // Start progressive loading
            startProgressiveLoading();
        }
        
        // Cleanup function
        return () => {
            cancelLoadingRef.current = true;
            timeoutRefs.current.forEach(clearTimeout);
            timeoutRefs.current = [];
        };
    }, [flattenedItems, startProgressiveLoading]);

    // Cleanup on navigation (parent/type change)
    useEffect(() => {
        return () => {
            cancelLoadingRef.current = true;
            loadingRef.current = false;
            timeoutRefs.current.forEach(clearTimeout);
            timeoutRefs.current = [];
        };
    }, [parent, type]);

    // Toggle node expansion
    const toggleNode = useCallback((nodeValue) => {
        
        setExpandedNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(nodeValue)) {
                newSet.delete(nodeValue);
            } else {
                newSet.add(nodeValue);
            }
            return newSet;
        });

        // Handle URL hash and store updates
        if (type === 'requestexpanded') {
            const expandedFlag = getDescriptionExpansionAlertFlag();
            putDataInDevCenterDataStore({
                type: 'UPDATE_DESCRIPTION_EXPANSION_ALERT_FLAG', 
                descriptionExpansionAlertFlag: !expandedFlag
            });
        }

        const urlHash = replaceSpace(getFirstLevelKey()).replaceAll("_","-") +"_" + 
                       replaceSpace(getSecondLevelKey()).replaceAll("_","-") +"_"+ 
                       replaceSpace(getThirdLevelKey()).replaceAll("_","-")+"_" + 
                       type +"fielddescription_" + nodeValue.replaceAll("-","_");
        setWindowLocationHash(urlHash.replace(/-_/g,"_"));
    }, [type]);
    
    if (!jsTree || !Array.isArray(jsTree) || jsTree.length === 0) {
        return <div></div>;
    }

    return (
        <div 
            className={type + '-' + parent} 
            style={{ display: display }}
        >
            
            <div
                style={{
                    overflowY: 'auto',
                    position: 'relative'
                }}
            >
                {flattenedItems.map((comment, index) => {
                    // Render all items immediately - no loading states
                    const isExpanded = expandedNodes.has(comment.value);
                    const borderInfo = getBorderInfo(comment);
                    
                    return (
                        <div 
                            key={comment.value}
                            className='descriptions'
                            style={{ 
                                marginLeft: `${comment.level * 20}px`
                            }}
                        >
                            <div 
                                id={'hierarchy-' + type + '-' + comment.value} 
                                style={{
                                    marginLeft: '10px',
                                    borderLeft: borderInfo.showOwnBorder ? '1px solid rgb(217,217,214)' : 'unset',
                                    position: 'relative'
                                }}
                            >
                                {borderInfo.parentBorders.map((parentBorder, borderIndex) => (
                                    <div 
                                        key={`border-${parentBorder.parentValue}-${borderIndex}`}
                                        style={{
                                            position: 'absolute',
                                            left: `${parentBorder.offset}px`,
                                            top: 0,
                                            bottom: 0,
                                            borderLeft: '1px solid rgb(217,217,214)',
                                            pointerEvents: 'none',
                                            zIndex: 1
                                        }}
                                    />
                                ))}
                                <div 
                                    id={type + '-' + comment.value} 
                                    style={{ marginRight: '5px', marginLeft: '15px', paddingTop: '5px', paddingBottom: '5px' }}
                                >
                                    {comment.children && (
                                        <span>
                                            <div 
                                                onClick={() => toggleNode(comment.value)}
                                                style={{
                                                    marginRight: '10px', 
                                                    cursor: 'pointer', 
                                                    display: 'inline-block'
                                                }}
                                            >
                                                <FontAwesomeIcon 
                                                    icon={isExpanded ? faChevronUp : faChevronDown} 
                                                    size="xs"
                                                />
                                            </div>
                                        </span>
                                    )}
                                    
                                    <span 
                                        role="button" 
                                        className='descriptionsLabel' 
                                        tabIndex="1" 
                                        onClick={() => {
                                            if (comment.children) {
                                                toggleNode(comment.value);
                                            }
                                            const urlHash = replaceSpace(getFirstLevelKey()).replaceAll("_","-") +"_" + 
                                                           replaceSpace(getSecondLevelKey()).replaceAll("_","-") +"_"+ 
                                                           replaceSpace(getThirdLevelKey()).replaceAll("_","-")+"_" + 
                                                           type +"fielddescription_" + comment.value.replaceAll("-","_");
                                            setWindowLocationHash(urlHash.replace(/-_/g,"_"));
                                        }}
                                        style={{ 
                                            marginLeft: comment.children ? "0px" : "19.19px",
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {(typeof comment.label === 'string') ? comment.label : comment.label?.props?.children?.[0] || comment.value}
                                    </span>
                                    
                                    {comment.additionalInformation && (
                                        <span onClick={() => {
                                            putDataInDevCenterDataStore({type: 'UPDATE_NODE_CLICK_CHECK', nodeClickCheck: true});
                                            putDataInDevCenterDataStore({type: 'UPDATE_CLICKED_NODE', clickedNode: type+'-body-'+comment.value});
                                        }}>
                                            <CybsIcon name='info' resolution='tiny' dataTest='info'/>
                                        </span>
                                    )}
                                    
                                    <br />
                                    
                                    {/* Parameter information display */}
                                    <div style={{marginLeft:'20px', display: 'block'}}>
                                        {comment.type && <span><span style={{fontWeight:'bold'}}>{'Type  '}</span>{comment.type}</span>}
                                        
                                        {comment.minLength && 
                                            <span style={{marginLeft:'20px'}}>
                                                <span style={{fontWeight:'bold'}}>{'MinLength  '}</span>
                                                {comment.minLength}
                                            </span>
                                        }
                                        
                                        {comment.maxLength &&
                                            <span style={{marginLeft:'20px'}}>
                                                <span style={{fontWeight:'bold'}}>{'MaxLength  '}</span>
                                                {comment.maxLength}
                                            </span>
                                        }
                                        
                                        {comment.pattern &&
                                            <span style={{marginLeft:'20px'}}><span style={{fontWeight:'bold'}}>{'Pattern  '}</span>{comment.pattern}</span>
                                        }
                                        
                                        {comment.className === "custom-required" &&
                                            <span style={{marginLeft:'20px'}}><span className="required-description">Required</span></span>
                                        }
                                        
                                        {comment.className === "required" && 
                                            <span style={{marginLeft:'20px'}}><span style={{fontWeight:'bold', color:'red'}}>Required</span></span>
                                        }

                                        {comment.type && <br />}
                                    </div>
                                    
                                    <div>
                                        <span dangerouslySetInnerHTML={{
                                            __html: processedDescriptions.has(comment.value) 
                                                ? processedDescriptions.get(comment.value)
                                                : DOMPurify.sanitize(marked(comment.description === undefined ? "" : comment.description.replaceAll(/Cybersource/ig, partnerFullName)))
                                        }} />
                                    </div>
                                    
                                    <hr color="#F4F4F4" style={{margin: '10px 0'}} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
        </div>
    );
});

export default ProgressiveDescriptions;
